/* eslint-disable react-hooks/refs --
 * AuthProvider намеренно императивен и не вписывается в модель React Compiler:
 * токены живут в refs (не в state), а configureClient() вызывается СИНХРОННО во
 * время рендера — чтобы дети успели подхватить геттеры до первого запроса (см.
 * CLAUDE.md, «Auth: refs, не state»). Перенос в useEffect — единственный способ
 * удовлетворить react-hooks/refs — прямо запрещён: дочерние компоненты начнут
 * запросы до конфигурации клиента. Поэтому правило здесь отключено осознанно.
 */
import { createContext, useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { configureClient } from '@/api/client'
import { logout as apiLogout } from '@/api/auth'
import type { TokenPairResponse } from '@/api/types'
import {
  clearIsAdmin,
  clearRefresh,
  loadIsAdmin,
  loadRefresh,
  saveIsAdmin,
  saveRefresh,
} from './storage'

export interface AuthState {
  isAuthenticated: boolean
  isAdmin: boolean
  login: (pair: TokenPairResponse) => void
  logout: () => Promise<void>
  // Возвращает текущий access-токен (или null) без подписки на ре-рендеры.
  // Использовать для bootstrap WS-handshake, где токен передаётся через query.
  getAccessToken: () => string | null
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthState | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

function extractIsAdmin(accessToken: string): boolean {
  try {
    const b64 = accessToken.split('.')[1]
    const json = atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(json) as Record<string, unknown>
    return payload.is_admin === true
  } catch {
    return false
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Refs всегда содержат актуальные значения — никаких stale closures
  // в callback-ах, которые передаются в configureClient().
  const accessRef = useRef<string | null>(null)
  const refreshRef = useRef<string | null>(loadRefresh())
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(refreshRef.current !== null)
  // Инициализируем из кеша — без мигания при перезагрузке страницы.
  // Перезаписывается реальным значением из JWT при первом refresh/login.
  const [isAdmin, setIsAdmin] = useState<boolean>(loadIsAdmin)

  // configureClient вызывается СИНХРОННО во время рендера (не в useEffect),
  // чтобы дочерние компоненты успели подхватить актуальные геттеры
  // до первого запроса. Колбэки замыкаются на refs — всегда свежие.
  const installRef = useRef(false)
  if (!installRef.current) {
    installRef.current = true
    configureClient({
      getAccessToken: () => accessRef.current,
      getRefreshToken: () => refreshRef.current,
      onTokenRefreshed: (pair) => {
        accessRef.current = pair.accessToken
        refreshRef.current = pair.refreshToken
        saveRefresh(pair.refreshToken)
        const admin = extractIsAdmin(pair.accessToken)
        saveIsAdmin(admin)
        setIsAdmin(admin)
      },
      onSessionExpired: (code) => {
        accessRef.current = null
        refreshRef.current = null
        clearRefresh()
        clearIsAdmin()
        setIsAuthenticated(false)
        setIsAdmin(false)
        if (code === 'USER_BANNED') {
          try {
            // Код, а не текст: LoginPage переводит его на языке, активном в момент показа.
            sessionStorage.setItem('podpipe.login_notice', code)
          } catch {
            // приватный режим
          }
        }
      },
    })
  }

  const login = useCallback((pair: TokenPairResponse) => {
    accessRef.current = pair.accessToken
    refreshRef.current = pair.refreshToken
    saveRefresh(pair.refreshToken)
    const admin = extractIsAdmin(pair.accessToken)
    saveIsAdmin(admin)
    setIsAdmin(admin)
    setIsAuthenticated(true)
  }, [])

  const logout = useCallback(async () => {
    const refreshToken = refreshRef.current
    accessRef.current = null
    refreshRef.current = null
    clearRefresh()
    clearIsAdmin()
    setIsAuthenticated(false)
    setIsAdmin(false)
    if (refreshToken) {
      try {
        await apiLogout(refreshToken)
      } catch {
        // даже если сервер вернул ошибку — локально мы уже разлогинились
      }
    }
  }, [])

  const getAccessToken = useCallback(() => accessRef.current, [])

  const value = useMemo<AuthState>(
    () => ({ isAuthenticated, isAdmin, login, logout, getAccessToken }),
    [isAuthenticated, isAdmin, login, logout, getAccessToken],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
